import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useExperiment } from '../../contexts/ExperimentContext';
import { useTracker } from '../../contexts/TrackerContext';
import { completeExperiment } from '../../services/api';
import './Debrief.css';

interface SecurityTip {
  title: string;
  description: string;
  example?: string;
  icon: string;
}

const Debrief: React.FC = () => {
  const navigate = useNavigate();
  const { experimentData, updateProgress } = useExperiment();
  const { tracker } = useTracker();
  
  const [acknowledgment, setAcknowledgment] = useState({
    understandPurpose: false,
    understandDeception: false,
    receivedEducation: false,
    consentToData: false
  });
  
  const [feedbackProvided, setFeedbackProvided] = useState(false);
  const [feedback, setFeedback] = useState('');
  
  useEffect(() => {
    tracker.track('phaseStart', { phase: 'debrief' });
  }, [tracker]);

  // Completion metadata only. The study deliberately does not display a score:
  // cautious verification, skipping, and reporting are distinct behaviors and
  // cannot be reduced to a link-click percentage without misrepresenting them.
  const studySummary = {
    emailDecisions: (tracker.events || []).filter(e => e.eventType === 'emailDecision').length,
    deviceDecisions: (tracker.events || []).filter(e => e.eventType === 'deviceAction').length
  };

  // Security education content covering both experimental task families.
  const securityTips: SecurityTip[] = [
    {
      title: "Check the Sender's Email Domain",
      description: "Phishing emails often come from domains that look similar to legitimate ones but have subtle misspellings or extra characters.",
      example: "companyy.com instead of company.com, or microsoft-notify.com instead of microsoft.com",
      icon: "📧"
    },
    {
      title: "Be Wary of Urgency and Threats",
      description: "Urgency is a cue to slow down and verify, not proof by itself that a message is fraudulent. Legitimate organizations sometimes communicate real deadlines.",
      example: "Confirm the request through an official portal or a known contact before taking a sensitive action.",
      icon: "⚠️"
    },
    {
      title: "Hover Over Links Before Clicking",
      description: "Always hover over links to see the actual URL destination. Phishing links often use URL shorteners or misleading domains.",
      example: "A link that says 'Update Password' but goes to http://bit.ly/xyz instead of your company's actual domain",
      icon: "🔗"
    },
    {
      title: "Verify Unexpected Requests",
      description: "If you receive an unexpected request, especially for sensitive information or urgent actions, verify through a separate channel.",
      example: "Call the supposed sender directly using a known phone number, not one provided in the email",
      icon: "📞"
    },
    {
      title: "Look for Generic Greetings",
      description: "Phishing emails often use generic greetings because they don't know your actual name.",
      example: "Dear Customer or Dear User instead of your actual name",
      icon: "👤"
    },
    {
      title: "Check for Poor Grammar and Spelling",
      description: "While sophisticated attacks may have perfect grammar, many phishing emails contain obvious errors.",
      example: "Spelling mistakes, awkward phrasing, or unprofessional language",
      icon: "📝"
    },
    {
      title: "Check Software and Device Identity",
      description: "Before installing software, approving a device, or granting permissions, confirm the publisher, source, and requested access through a trusted channel.",
      example: "Use your organization's approved software catalog or confirm an unexpected sign-in prompt with IT.",
      icon: "💻"
    },
    {
      title: "Protect Connections and External Media",
      description: "Treat unknown USB devices and unexpected connection requests cautiously. On legitimate public WiFi, use your organization's approved VPN and avoid sensitive activity.",
      example: "Confirm a public network name against venue signage, then enable the approved VPN before connecting.",
      icon: "🔐"
    }
  ];

  const handleCheckboxChange = (field: keyof typeof acknowledgment) => {
    setAcknowledgment(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const allAcknowledged = Object.values(acknowledgment).every(val => val === true);

  const handleComplete = async () => {
    if (!allAcknowledged) return;

    tracker.track('debriefComplete', {
      acknowledgments: acknowledgment,
      feedbackProvided,
      feedback: feedback || null,
      studySummary
    });

    updateProgress(100); // 100% complete

    // A leftover target would send this participant to someone else's credit link
    // if the call below fails.
    ['prolificCompletionUrl', 'prolificCompletionCode', 'completionPlatform', 'completionFailed']
      .forEach(key => localStorage.removeItem(key));

    // Mark experiment as complete in backend. Credit depends on this response, so a
    // brief network failure gets retried before the participant is moved on.
    const participantId = experimentData?.participant?.id;
    let completed = false;
    for (let attempt = 0; participantId && !completed && attempt < 3; attempt++) {
      try {
        if (attempt > 0) await new Promise(resolve => setTimeout(resolve, 1500 * attempt));
        const res = await completeExperiment(participantId, {
          acknowledgments: acknowledgment,
          feedback: feedback || null,
          studySummary
        });
        completed = true;
        // Persist the server-released completion target so /complete can redirect.
        try {
          if (res?.completionUrl) localStorage.setItem('prolificCompletionUrl', res.completionUrl);
          if (res?.completionCode) localStorage.setItem('prolificCompletionCode', res.completionCode);
          if (res?.completionPlatform) localStorage.setItem('completionPlatform', res.completionPlatform);
        } catch {}
      } catch (error) {
        console.error('Failed to mark experiment as complete:', error);
      }
    }
    // Continue anyway so the participant is not stuck, but /complete must say so.
    if (!completed) localStorage.setItem('completionFailed', 'true');

    // Clear experiment session
    localStorage.removeItem('experimentSession');
    localStorage.removeItem('participantId');
    
    // Navigate to completion page
    navigate('/complete');
  };

  return (
    <div className="debrief-phase">
      <div className="debrief-content">
        <h1>Study Debriefing</h1>
        
        {/* True Purpose Revelation */}
        <section className="debrief-section purpose-section">
          <h2>The True Purpose of This Study</h2>
          <div className="purpose-content">
            <p>
              Thank you for participating in our research. We can now reveal that this study was investigating 
              <strong> how people use overall impressions and specific details when making email and
              device-security decisions</strong>.
            </p>
            <p>
              The tasks contained simulated legitimate and risky situations. In some scenarios, the
              overall appearance and the specific details pointed in the same direction; in others,
              those cues conflicted. This research helps us understand:
            </p>
            <ul>
              <li>How people respond when quick impressions and concrete details agree or conflict</li>
              <li>When people engage, skip, verify, report, proceed, or decline</li>
              <li>How people explain the reasons for their decisions</li>
              <li>Whether decision patterns carry across email and device-security contexts</li>
            </ul>
            <p className="important-note">
              <strong>Important:</strong> No actual email, download, connection, or security breach occurred.
              All situations were simulated and contained within the study environment. Your computer and
              accounts remain secure.
            </p>
          </div>
        </section>

        {/* Security Education */}
        <section className="debrief-section education-section">
          <h2>Practical Email and Device-Security Guidance</h2>
          <p>Here are key strategies for handling email and workplace technology safely:</p>
          
          <div className="security-tips">
            {securityTips.map((tip, index) => (
              <div key={index} className="security-tip">
                <div className="tip-header">
                  <span className="tip-icon">{tip.icon}</span>
                  <h3>{tip.title}</h3>
                </div>
                <p className="tip-description">{tip.description}</p>
                {tip.example && (
                  <div className="tip-example">
                    <strong>Example:</strong> {tip.example}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Remember Section */}
        <section className="debrief-section remember-section">
          <h2>Remember</h2>
          <div className="remember-content">
            <ul>
              <li>
                <strong>When in doubt, don't click.</strong> It's better to verify through another channel 
                than to risk a security breach.
              </li>
              <li>
                <strong>Urgency is a signal to verify.</strong> A deadline can be real or deceptive, so
                confirm sensitive requests through a trusted channel rather than judging on urgency alone.
              </li>
              <li>
                <strong>Use a gut feeling as a prompt to check.</strong> If something feels wrong,
                investigate the source and details through a trusted channel before acting.
              </li>
              <li>
                <strong>Use your organization's security procedures.</strong> Report suspicious
                messages and ask IT about unexpected software, device, or connection prompts.
              </li>
            </ul>
          </div>
        </section>

        {/* Acknowledgments */}
        <section className="debrief-section acknowledgment-section">
          <h2>Study Acknowledgments</h2>
          <p>Please confirm that you understand the following:</p>
          
          <div className="acknowledgment-list">
            <label className="acknowledgment-item">
              <input
                type="checkbox"
                checked={acknowledgment.understandPurpose}
                onChange={() => handleCheckboxChange('understandPurpose')}
              />
              <span>I understand the true purpose of this study was to investigate email and device-security decision-making</span>
            </label>
            
            <label className="acknowledgment-item">
              <input
                type="checkbox"
                checked={acknowledgment.understandDeception}
                onChange={() => handleCheckboxChange('understandDeception')}
              />
              <span>I understand that deception was necessary for valid research results</span>
            </label>
            
            <label className="acknowledgment-item">
              <input
                type="checkbox"
                checked={acknowledgment.receivedEducation}
                onChange={() => handleCheckboxChange('receivedEducation')}
              />
              <span>I have read and understood the security education provided</span>
            </label>
            
            <label className="acknowledgment-item">
              <input
                type="checkbox"
                checked={acknowledgment.consentToData}
                onChange={() => handleCheckboxChange('consentToData')}
              />
              <span>I understand that my confidential study data will be used for research as described in the consent form</span>
            </label>
          </div>
        </section>

        {/* Optional Feedback */}
        <section className="debrief-section feedback-section">
          <h2>Your Feedback (Optional)</h2>
          <p>We value your thoughts about this study. Please share any feedback you have:</p>
          
          <textarea
            className="feedback-textarea"
            rows={4}
            placeholder="Share your thoughts about the study, what you learned, or any suggestions..."
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            onBlur={() => setFeedbackProvided(feedback.length > 0)}
          />
        </section>

        {/* Complete Button */}
        <div className="debrief-actions">
          <button
            className={`btn btn-primary ${!allAcknowledged ? 'disabled' : ''}`}
            onClick={handleComplete}
            disabled={!allAcknowledged}
          >
            Complete Study
          </button>
          {!allAcknowledged && (
            <p className="completion-note">
              Please check all acknowledgments before completing the study.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Debrief;
